import { format } from "date-fns/format";

/**
 * Wait for a given time.
 * @param delay wait time in ms
 * @returns a promise that will resolve after the delay
 */
export function sleep(delay: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, delay));
}

/**
 * Is this a string ?
 * @param s the object to check
 * @returns true if the given object is a string otherwise returns false
 */
export function isString(s: any) {
  return typeof s === "string" || s instanceof String;
}

/**
 * Remap a number from a range to another.
 * @param x the number to remap
 * @param fromMin the min of the source range
 * @param fromMax the max of the source range
 * @param toMin the min of the destination range
 * @param toMax the max of the destination range
 * @returns the remapped number
 */
export function remap(
  x: number,
  fromMin: number,
  fromMax: number,
  toMin: number,
  toMax: number
): number {
  return ((x - fromMin) * (toMax - toMin)) / (fromMax - fromMin) + toMin;
}

/**
 * Return evenly spaced numbers over a closed interval [min, max].
 * @param min start of interval
 * @param max end of interval
 * @param count number of samples to generate
 * @returns array of evenly spaced values (empty when count <= 0)
 */
export function linspace(min: number, max: number, count: number): number[] {
  if (count <= 0) {
    return [];
  }
  if (count === 1) {
    return [min];
  }
  const step = (max - min) / (count - 1);
  return Array.from({ length: count }, (_, index) => min + index * step);
}

/**
 * Hash a message using SHA 1
 * @param message the message to hash
 * @param algorithm: AlgorithmIdentifier
 * @returns the hashed message as an hex string
 */
export async function sha(
  message: string | BufferSource,
  algorithm: AlgorithmIdentifier = "SHA-1"
) {
  const data = typeof message === "string" ? new TextEncoder().encode(message) : message;
  const hashBuffer = await crypto.subtle.digest(algorithm, data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * Format a byte count as a short file size.
 * @param size the size in bytes
 * @returns a size such as "8 B" or "1.5 KB", or null when size is missing or negative
 */
export function humanizeFileSize(size: number | null): string | null {
  if (size === null || size < 0) return null;
  const units = ["B", "KB", "MB", "GB", "TB"];
  let value = size;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const rounded = unit === 0 ? value : Math.round(value * 10) / 10;
  const amount = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  return `${amount} ${units[unit]}`;
}

/**
 * Save a blob to the user's file system by triggering a download.
 * @param blob the blob to save
 * @param filename the filename to be downloaded
 */
export function saveBlob(blob: Blob, filename: string) {
  const a = document.createElement("a");
  const url = window.URL.createObjectURL(blob);

  a.setAttribute("style", "display: none");
  a.href = url;
  a.download = filename;

  document.body.appendChild(a);
  a.click();

  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

/**
 * Run a given function immediately then run it periodically.
 * @param fn the function to run
 * @param interval the delay in ms between each call
 * @returns a function that when called stops the polling.
 */
export async function poll(fn: () => void, interval: number): Promise<() => void> {
  let intervalId: number = -1;

  const start = () => {
    intervalId = window.setInterval(fn, interval);
  };

  const stop = () => {
    if (intervalId) {
      window.clearInterval(intervalId);
      intervalId = -1;
    }
  };

  await fn();
  start();

  return stop;
}

/**
 * Deep compare two objects.
 * @param a
 * @param b
 * @returns true if object are equals false otherwise
 */
export function isDeepEqual(a: object, b: object) {
  const sa = JSON.stringify(a);
  const sb = JSON.stringify(b);
  return sa == sb;
}

/**
 * Is the user in dark mode ? 🧛🏻‍♀️
 * @returns true if the user prefers dark mode, false otherwise.
 */
export function isUserInDarkMode() {
  return window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
}

/**
 * Swap two items in an array.
 *
 * This function does the swap in place
 * and fails silently if you pass out of range indexes.
 * @param a the array
 * @param srcIndex the index of the item to swap
 * @param dstIndex the index where you want the item to be swapped
 */
export function swapItemsInArray(a: any[], srcIndex: number, dstIndex: number) {
  if (srcIndex < 0 || srcIndex > a.length || dstIndex < 0 || dstIndex > a.length) {
    console.error(
      "Cant swap item in array because some index are out of range",
      srcIndex,
      dstIndex
    );
    return;
  }
  a.splice(dstIndex, 0, a.splice(srcIndex, 1)[0]);
}

/**
 * Generate a random number which is a multiple of a given integer.
 * @param baseNumber The number for which you want to generate multiples
 * @param min The lowest number that can be generated.
 * @param max The highest number that can be generated.
 * @returns a random number
 */
export function generateRandomMultiple(baseNumber: number, min: number, max: number) {
  const multiplier = Math.floor(Math.random() * (max - min + 1)) + min;

  return baseNumber * multiplier;
}

/**
 * Generate a random id.
 * @returns a random id
 */
export function generateRandomId() {
  return crypto.randomUUID();
}

/**
 * Debounce a function.
 *
 * Ported to TS from [You-Dont-Need-Lodash](https://github.com/you-dont-need/You-Dont-Need-Lodash-Underscore?tab=readme-ov-file#_debounce)
 * @param func the function to debounce
 * @param wait the delay in ms
 * @param immediate if true the function will be called immediately
 * @returns the debounced function
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
  immediate: boolean
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  return function (this: any, ...args: any[]) {
    if (timeout) clearTimeout(timeout);
    if (immediate && !timeout) func.apply(this, [...args]);
    timeout = setTimeout(function (this: any) {
      timeout = null;
      if (!immediate) func.apply(this, [...args]);
    }, wait);
  };
}

/**
 * From an unknown this function does its best to return a message.
 * @param error the unknown error
 * @returns an error string
 */
export function getErrorMessage(error: unknown) {
  if (error instanceof Error) return error.message;
  return String(error);
}

/**
 * Check if an element overflows.
 * @param element the dom element to check
 * @returns true if the elements overflows false otherwise
 */
export function isElementOverflowing(element: Element) {
  const { scrollHeight, clientHeight, scrollWidth, clientWidth } = element;
  return scrollHeight > clientHeight || scrollWidth > clientWidth;
}

/**
 * Download from an url and returns the response as a base64 string
 * @param url the url to download from
 * @returns a base 64 string
 */
export async function urlToBase64(url: string): Promise<string> {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.statusText}`);
    }

    const arrayBuffer = await response.arrayBuffer();
    const bytes = new Uint8Array(arrayBuffer);

    // Convert bytes to base64
    let binary = "";
    bytes.forEach((byte) => (binary += String.fromCharCode(byte)));

    return btoa(binary);
  } catch (error) {
    throw new Error(`Error converting image to base64: ${error}`);
  }
}

export function isIntegerDtype(dtype: string): boolean {
  return /int/i.test(dtype) && /(?<![0-9])(8|16|32|64)(?![0-9])/.test(dtype);
}

export function isDatetimeDtype(dtype: string): boolean {
  return /datetime/i.test(dtype);
}

/**
 * Format a number (integer or float) as a string with appropriate precision.
 *
 * For integer dtypes (e.g. int64, uint8): returns a rounded integer string.
 * For floats, determines precision dynamically:
 * - Returns "0" for zero
 * - Uses `decimals` decimal places for numbers >= 0.01 (default 2)
 * - For smaller positive numbers, uses as many decimal places as needed to show the first significant digit
 * - If magnitude (digits before the first significant digit) exceeds `maxMagnitude`, uses scientific notation
 * @param value the number to format
 * @param options.decimals default number of decimal places for floats (default 2)
 * @param options.maxMagnitude max magnitude before switching to scientific notation (default 3)
 * @param options.dtype optional column dtype; when an integer type, value is formatted as an integer (no decimals)
 * @returns string representation: integer when dtype is integer, otherwise decimal or scientific as appropriate
 */
export function formatNumber(
  value: number,
  {
    decimals = 2,
    maxMagnitude = 3,
    dtype,
  }: { decimals?: number; maxMagnitude?: number; dtype?: string } = {}
) {
  if (dtype !== undefined && isIntegerDtype(dtype)) {
    if (!Number.isFinite(value)) return String(value);
    return String(Math.round(value));
  }
  if (value === 0) return "0";
  const absValue = Math.abs(value);
  if (absValue >= 0.01) return value.toFixed(decimals);
  const magnitude = Math.floor(Math.log10(absValue));
  if (magnitude * -1 > maxMagnitude) {
    return value.toExponential(decimals);
  }
  return value.toFixed(Math.abs(magnitude));
}

/**
 * Return an array that is the intersection of the two given array
 * ©barbosa https://stackoverflow.com/questions/1885557/simplest-code-for-array-intersection-in-javascript
 */
export function intersect(a: any[], b: any[]) {
  const setA = new Set(a);
  const setB = new Set(b);
  const intersection = new Set([...setA].filter((x) => setB.has(x)));
  return Array.from(intersection);
}

/**
 * Function mostly written by an LLM
 * Try to find user's OS
 * @returns a string containing the OS name
 */
export function detectUserOS() {
  // Try modern API first (synchronous)
  if ("userAgentData" in navigator) {
    const uad: any = navigator.userAgentData;
    return uad.platform;
  }

  // Fall back to user agent parsing
  const userAgent = navigator.userAgent;

  if (/Windows NT/i.test(userAgent)) return "Windows";
  if (/Mac OS X/i.test(userAgent) || /Macintosh/i.test(userAgent)) return "macOS";
  if (/Linux/i.test(userAgent) && !/Android/i.test(userAgent)) return "Linux";
  if (/Android/i.test(userAgent)) return "Android";
  if (/iPhone|iPad|iPod/i.test(userAgent)) return "iOS";
  if (/CrOS/i.test(userAgent)) return "Chrome OS";

  return "Unknown";
}

/**
 * Compute the ratio between the largest and smallest inter-point distances.
 *
 * A value larger than 5 typically indicates that the parameter range would
 * better be displayed with a log scale while a linear scale would be more
 * suitable otherwise.
 *
 * Ported by claude from https://github.com/scikit-learn/scikit-learn/blob/d171a3c0422b854c4dd63c0c78e4468f71a65dd5/sklearn/utils/_plotting.py#L280
 *
 * @param data - Array of numeric values
 * @returns Ratio of max difference to min difference
 */
export function intervalMaxMinRatio(data: number[]) {
  // Sort the data array
  const sorted = [...data].sort((a, b) => a - b);

  // Calculate differences between consecutive elements
  const diff = [];
  for (let i = 1; i < sorted.length; i++) {
    diff.push(sorted[i] - sorted[i - 1]);
  }

  // Find max and min differences
  const maxDiff = Math.max(...diff);
  const minDiff = Math.min(...diff);

  return maxDiff / minDiff;
}

/**
 * Slugify string.
 *
 * The string will be lower-case and contain only ASCII letters, digits, and
 * characters `.`, `-`, and `_`.
 *
 * In order:
 * - convert to ASCII and ignore characters in error,
 * - replace characters that aren't alphanumerics, dots, dashes or underscores by
 *   dash,
 * - convert repeated dots to single dots,
 * - convert repeated dashes to single dash,
 * - convert repeated underscores to single underscore,
 * - strip leading and trailing dots, dashes, and underscores.
 */
export function slugify(name: string): string {
  let string = name.normalize("NFKD").replace(/[^\x00-\x7F]/g, "");
  string = string.toLowerCase();
  string = string.replace(/[^\w.-]/g, "-");
  string = string.replace(/\.+/g, ".");
  string = string.replace(/-+/g, "-");
  string = string.replace(/_+/g, "_");

  return string.replace(/^[._-]+|[._-]+$/g, "");
}

/**
 * Add the appropriate ordinal suffix to a number (e.g., 1st, 2nd, 3rd, 4th, etc.).
 * Handles special cases for numbers ending in 11, 12, or 13.
 * @param value The number to format with an ordinal suffix.
 * @returns The number as a string with its ordinal suffix.
 */
export function addOrdinalSuffix(value: number): string {
  const suffixes = ["th", "st", "nd", "rd"];
  const v = value % 100;

  if (v >= 11 && v <= 13) {
    return `${value}th`;
  }

  return `${value}${suffixes[v % 10] || "th"}`;
}

/**
 * Get a cookie by name
 * @param name the name of the cookie
 * @returns the value of the cookie or null if the cookie is not found
 */
export function getCookie(name: string) {
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? decodeURIComponent(match[2] ?? "") : null;
}

/**
 * Set a cookie by name
 * @param name the name of the cookie
 * @param value the value of the cookie
 * @param options.expires the expiration date of the cookie
 * @param options.path the path of the cookie
 */
export function setCookie(
  name: string,
  value: string,
  options: { expires?: Date; path?: string } = {}
) {
  document.cookie = `${name}=${value}; path=${options.path ?? "/"}; expires=${options.expires?.toUTCString() ?? ""}`;
}

/**
 * Format a datetime string to a human-readable format.
 * @param dt the datetime string to format
 * @param showTime whether to include hours and minutes (default: true)
 * @returns a formatted datetime string
 */
export function formatDatetime(dt: string | null | undefined, showTime: boolean = true): string {
  if (dt == null || dt === "") {
    return "Never";
  }
  if (showTime) {
    return format(new Date(dt), "d MMMM yyyy HH:mm");
  }
  return format(new Date(dt), "d MMMM yyyy");
}
